import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from './lib/auth';
import { Login } from './pages/Login';
import { SignInComplete } from './pages/SignInComplete';
import { Appointments } from './pages/ehealth/Appointments';
import { ClinicalRecords } from './pages/ehealth/ClinicalRecords';
import { Facilities } from './pages/ehealth/Facilities';
import { Labs } from './pages/ehealth/Labs';
import { Patients } from './pages/ehealth/Patients';
import { Prescriptions } from './pages/ehealth/Prescriptions';
import { Referrals } from './pages/ehealth/Referrals';
import { Applications } from './pages/publichealth/Applications';
import { Complaints } from './pages/publichealth/Complaints';
import { FoodHandlerCerts } from './pages/publichealth/FoodHandlerCerts';
import { GradeRegister } from './pages/publichealth/GradeRegister';
import { ImportPermits } from './pages/publichealth/ImportPermits';
import { Inspections } from './pages/publichealth/Inspections';
import { Surveillance } from './pages/publichealth/Surveillance';
import { Shell } from './ui/Shell';

export function App() {
  const { persona } = useAuth();

  if (!persona) {
    return (
      <Routes>
        <Route path="/signin/complete" element={<SignInComplete />} />
        <Route path="*" element={<Login />} />
      </Routes>
    );
  }

  // Two workspaces, not one-per-role — land each persona somewhere useful
  // for their primary duty within their workspace (same idiom as
  // servicedesk-portal's landing-by-role, applied per-workspace here since
  // the two role families are disjoint).
  const roles = persona.roles;
  const ehealthLanding = roles.includes('RECEPTION')
    ? '/ehealth/appointments'
    : roles.includes('PHARMACIST')
      ? '/ehealth/prescriptions'
      : roles.includes('LAB_PARTNER')
        ? '/ehealth/labs'
        : roles.includes('TREATMENT_BOARD')
          ? '/ehealth/referrals'
          : '/ehealth/patients';
  const publicHealthLanding = roles.includes('PORT_HEALTH_OFFICER')
    ? '/public-health/import-permits'
    : roles.includes('EPIDEMIOLOGIST')
      ? '/public-health/surveillance'
      : '/public-health/applications';
  const landing = persona.workspace === 'public-health' ? publicHealthLanding : ehealthLanding;

  return (
    <Routes>
      <Route path="/" element={<Navigate to={landing} replace />} />

      <Route
        path="/ehealth"
        element={
          <Shell
            title="Patient Services & eHealth"
            items={[
              { to: '/ehealth/facilities', label: 'Facilities', icon: '⌂' },
              { to: '/ehealth/patients', label: 'Patient Lookup', icon: '◔' },
              { to: '/ehealth/appointments', label: 'Appointments', icon: '☰' },
              { to: '/ehealth/referrals', label: 'Referrals', icon: '⇄' },
              { to: '/ehealth/labs', label: 'Lab Orders', icon: '▤' },
              { to: '/ehealth/prescriptions', label: 'Prescriptions', icon: '✓' },
              { to: '/ehealth/records', label: 'Clinical Records', icon: '▧' },
            ]}
          />
        }
      >
        <Route index element={<Navigate to={ehealthLanding} replace />} />
        <Route path="facilities" element={<Facilities />} />
        <Route path="patients" element={<Patients />} />
        <Route path="appointments" element={<Appointments />} />
        <Route path="referrals" element={<Referrals />} />
        <Route path="labs" element={<Labs />} />
        <Route path="prescriptions" element={<Prescriptions />} />
        <Route path="records" element={<ClinicalRecords />} />
      </Route>

      <Route
        path="/public-health"
        element={
          <Shell
            title="Public Health Authority"
            items={[
              { to: '/public-health/applications', label: 'Health Cert Applications', icon: '▣' },
              { to: '/public-health/inspections', label: 'Inspections', icon: '◫' },
              { to: '/public-health/food-handler-certs', label: 'Food Handler Certs', icon: '✓' },
              { to: '/public-health/import-permits', label: 'Import Permits', icon: '⛴' },
              { to: '/public-health/complaints', label: 'Complaints', icon: '⚑' },
              { to: '/public-health/surveillance', label: 'Disease Surveillance', icon: '◈' },
              { to: '/public-health/grade-register', label: 'Grade Register', icon: '⛶' },
            ]}
          />
        }
      >
        <Route index element={<Navigate to={publicHealthLanding} replace />} />
        <Route path="applications" element={<Applications />} />
        <Route path="inspections" element={<Inspections />} />
        <Route path="food-handler-certs" element={<FoodHandlerCerts />} />
        <Route path="import-permits" element={<ImportPermits />} />
        <Route path="complaints" element={<Complaints />} />
        <Route path="surveillance" element={<Surveillance />} />
        <Route path="grade-register" element={<GradeRegister />} />
      </Route>

      <Route path="*" element={<Navigate to={landing} replace />} />
    </Routes>
  );
}
